import { useCallback, useEffect, useState } from 'react';
import { ADMIN_STATUS_LABEL, ORDER_STATUSES } from '@delivery/shared';
import { ApiFailure, BASE, api } from '../lib/api';
import type { PickupPointRow } from '../lib/types';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

interface Config {
  openApiEnabled: boolean;
  appId: string | null;
  hasSecret: boolean;
  secretHint: string | null;
  lastUsedAt: string | null;
  pickupPoint: { id: string; name: string; code: string } | null;
  webhookEnabled: boolean;
  webhookUrl: string | null;
  webhookEvents: string[];
}

interface Delivery {
  id: string;
  event: string;
  orderNo: string | null;
  orderStatus: string | null;
  status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'DEAD' | 'CANCELLED';
  attempts: number;
  lastHttpStatus: number | null;
  lastError: string | null;
  lastResponse: string | null;
  nextRetryAt: string | null;
  createdAt: string;
}

interface TestResult {
  ok: boolean;
  httpStatus: number | null;
  response: string;
  error: string | null;
  durationMs: number;
}

/** 推哪些状态 —— 草稿不会出现在商家那边,不给选 */
const PUSH_STATUSES = ORDER_STATUSES.filter((s) => s !== 'DRAFT');

const DELIVERY_LABEL: Record<Delivery['status'], string> = {
  QUEUED: '等待重试',
  RUNNING: '发送中',
  SUCCEEDED: '已送达',
  DEAD: '已放弃',
  CANCELLED: '没发(推送已关)',
};

/**
 * 一个商家的开放接口配置。
 *
 * ★ 放在商家这一行上,不单独一个「接口密钥」页:对方是谁、单挂在谁名下、
 *   推给谁,在这里是同一件事。分两处配的话,迟早出现「钥匙属于 A、推送配在 B」。
 *
 * ★ 平台总开关(「功能开关」里的「第三方下单接口」「订单状态推送」)关着时,
 *   这里配好了也不生效 —— 界面上要说清楚,不然他会以为是这里没配对。
 */
export function MerchantOpenApiDialog({
  customer,
  points,
  onClose,
}: {
  customer: { id: string; name: string };
  points: PickupPointRow[];
  onClose: () => void;
}) {
  /** 平台总开关的状态,没读到就是 undefined(那就不提示) */
  const [features, setFeatures] = useState<{ openApi: boolean; orderWebhook: boolean }>();
  const [cfg, setCfg] = useState<Config | null>(null);
  const [secret, setSecret] = useState<{ appId: string; appSecret: string } | null>(null);
  const [pointId, setPointId] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [test, setTest] = useState<TestResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const base = `/customers/${customer.id}/open-api`;

  const load = useCallback(async () => {
    try {
      const c = await api<Config>(base);
      setCfg(c);
      setPointId(c.pickupPoint?.id ?? '');
      setUrl(c.webhookUrl ?? '');
      setEvents(c.webhookEvents);
      setDeliveries(await api<Delivery[]>(`${base}/deliveries?limit=20`));
      const f = await api<Array<{ key: string; enabled: boolean }>>('/features').catch(() => null);
      if (f) {
        const on = (k: string) => f.find((x) => x.key === k)?.enabled ?? false;
        setFeatures({ openApi: on('openApi'), orderWebhook: on('orderWebhook') });
      }
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(name: string, fn: () => Promise<void>) {
    setBusy(name);
    setErr(null);
    setSaved(false);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '操作失败');
    } finally {
      setBusy(null);
    }
  }

  const save = (patch: Partial<Record<string, unknown>>) =>
    run('save', async () => {
      setCfg(await api<Config>(base, { method: 'PUT', body: patch }));
      setSaved(true);
    });

  const rotate = () =>
    run('secret', async () => {
      setSecret(
        await api<{ appId: string; appSecret: string }>(`${base}/secret`, { method: 'POST' }),
      );
      await load();
    });

  const sendTest = () =>
    run('test', async () => {
      setTest(await api<TestResult>(`${base}/webhook-test`, { method: 'POST', body: { url } }));
    });

  const retry = (d: Delivery) =>
    run(`retry-${d.id}`, async () => {
      await api(`${base}/deliveries/${d.id}/retry`, { method: 'POST' });
      await load();
    });

  const usablePoints = points.filter((p) => !p.customer || p.customer.publicId === customer.id);
  // 和管理端自己连的是同一台(按域名选,见 lib/api.ts)—— 商家照着它接入
  const apiBase = BASE.startsWith('http') ? BASE : `${location.origin.replace(/\/$/, '')}${BASE}`;

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${customer.name} 的开放接口`}
        className="w-full max-w-2xl max-h-[92dvh] overflow-y-auto rounded-2xl bg-white p-5
                   dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {customer.name} · 开放接口
          </h3>
          <button onClick={onClose} aria-label="关闭" className="text-xl text-slate-400">
            <InlineIcon icon={X} />
          </button>
        </div>

        {err && (
          <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {err}
          </div>
        )}

        {!cfg ? (
          <p className="text-sm text-slate-500">读取中…</p>
        ) : (
          <div className="space-y-5 text-sm">
            {/* ── 一、身份 ── */}
            <section className="space-y-2">
              <h4 className="font-medium text-slate-800 dark:text-slate-200">AppID 和 Secret</h4>
              {cfg.appId ? (
                <div className="grid grid-cols-[5rem_1fr] gap-y-1 text-slate-600 dark:text-slate-300">
                  <span className="text-slate-400">AppID</span>
                  <code className="break-all font-mono">{cfg.appId}</code>
                  <span className="text-slate-400">Secret</span>
                  <span className="font-mono">sk_…{cfg.secretHint?.replace('…', '')}</span>
                  <span className="text-slate-400">最近调用</span>
                  <span>
                    {cfg.lastUsedAt ? new Date(cfg.lastUsedAt).toLocaleString() : '还没调用过'}
                  </span>
                </div>
              ) : (
                <p className="text-slate-500">
                  还没生成。生成之后把 AppID 和 Secret 发给对方的技术。
                </p>
              )}
              {/*
                ★ 明文只出现这一次。说清楚,不然他关掉窗口再回来找就没了 ——
                  库里存的是密文,我们这边的界面也不会再显示。
              */}
              {secret && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
                  <div className="mb-1 font-medium text-amber-800 dark:text-amber-300">
                    Secret 只显示这一次,现在复制给对方
                  </div>
                  <div className="font-mono text-xs text-slate-800 dark:text-slate-100">
                    AppID:{secret.appId}
                    <br />
                    Secret:
                    <code aria-label="Secret 明文" className="break-all">
                      {secret.appSecret}
                    </code>
                  </div>
                  <button
                    onClick={() =>
                      void navigator.clipboard?.writeText(
                        `AppID: ${secret.appId}\nSecret: ${secret.appSecret}`,
                      )
                    }
                    className="mt-2 rounded border border-amber-400 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-300"
                  >
                    复制
                  </button>
                </div>
              )}
              <button
                onClick={() => void rotate()}
                disabled={busy !== null}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-slate-700 disabled:opacity-40 dark:border-slate-600 dark:text-slate-200"
              >
                {busy === 'secret'
                  ? '生成中…'
                  : cfg.hasSecret
                    ? '重置 Secret(旧的立刻失效)'
                    : '生成 AppID 和 Secret'}
              </button>
            </section>

            {/* ── 二、下单接口 ── */}
            <section className="space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800">
              <h4 className="font-medium text-slate-800 dark:text-slate-200">下单接口</h4>
              {features && !features.openApi && (
                <p className="text-amber-700 dark:text-amber-400">
                  平台的「第三方下单接口」总开关关着 —— 这里开了也调不通,先去「功能开关」打开。
                </p>
              )}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  aria-label="开放下单接口"
                  checked={cfg.openApiEnabled}
                  disabled={busy !== null || !cfg.hasSecret}
                  onChange={(e) => void save({ openApiEnabled: e.target.checked })}
                />
                <span>允许这家商家的系统调接口下单、查单</span>
              </label>
              <label className="block">
                <span className="mb-1 block text-slate-500">
                  默认取货点 —— 对方没传取货点编码、也没写发货地址时用它
                </span>
                <select
                  aria-label="默认取货点"
                  value={pointId}
                  onChange={(e) => {
                    setPointId(e.target.value);
                    void save({ apiPickupPointId: e.target.value || null });
                  }}
                  className={INPUT}
                >
                  <option value="">不设(用商家的默认取货地址)</option>
                  {usablePoints.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}({p.code})
                    </option>
                  ))}
                </select>
              </label>
              {usablePoints.length > 0 && (
                <p className="text-xs text-slate-500">
                  对方可以传的取货点编码:{usablePoints.map((p) => p.code).join('、')}
                </p>
              )}
            </section>

            {/* ── 三、状态推送 ── */}
            <section className="space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800">
              <h4 className="font-medium text-slate-800 dark:text-slate-200">状态推送</h4>
              {features && !features.orderWebhook && (
                <p className="text-amber-700 dark:text-amber-400">
                  平台的「订单状态推送」总开关关着 —— 这里配好了也不会推,先去「功能开关」打开。
                </p>
              )}
              <label className="block">
                <span className="mb-1 block text-slate-500">通知地址</span>
                <input
                  aria-label="通知地址"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://对方的域名/delivery/notify"
                  className={INPUT}
                />
              </label>
              <div>
                <div className="mb-1 text-slate-500">推哪些状态(都不勾 = 全部推)</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {PUSH_STATUSES.map((s) => (
                    <label key={s} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        aria-label={`推送${ADMIN_STATUS_LABEL[s]}`}
                        checked={events.includes(s)}
                        onChange={(e) =>
                          setEvents(
                            e.target.checked ? [...events, s] : events.filter((x) => x !== s),
                          )
                        }
                      />
                      {ADMIN_STATUS_LABEL[s]}
                      <span className="font-mono text-xs text-slate-400">{s}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  aria-label="开启状态推送"
                  checked={cfg.webhookEnabled}
                  disabled={busy !== null || !cfg.hasSecret}
                  onChange={(e) =>
                    void save({
                      webhookEnabled: e.target.checked,
                      webhookUrl: url,
                      webhookEvents: events,
                    })
                  }
                />
                <span>开启推送</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => void save({ webhookUrl: url || null, webhookEvents: events })}
                  disabled={busy !== null}
                  className="rounded-lg bg-brand-600 px-3 py-1.5 font-medium text-white disabled:opacity-40"
                >
                  {busy === 'save' ? '保存中…' : '保存地址和状态'}
                </button>
                <button
                  onClick={() => void sendTest()}
                  disabled={busy !== null || !url || !cfg.hasSecret}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-slate-700 disabled:opacity-40 dark:border-slate-600 dark:text-slate-200"
                >
                  {busy === 'test' ? '发送中…' : '发一条测试推送'}
                </button>
                {saved && (
                  <span className="self-center text-green-700 dark:text-green-400">已保存</span>
                )}
              </div>
              {test && (
                <div
                  className={`rounded-lg p-2 text-xs ${
                    test.ok
                      ? 'bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-300'
                      : 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300'
                  }`}
                >
                  {test.ok
                    ? `通了,对方回了 success(${test.durationMs} 毫秒)`
                    : `没通:${test.error}`}
                  {test.httpStatus != null && ` · HTTP ${test.httpStatus}`}
                  {test.response && (
                    <pre className="mt-1 whitespace-pre-wrap break-all font-mono">
                      对方回的:{test.response}
                    </pre>
                  )}
                </div>
              )}
            </section>

            {/* ── 四、推送记录 ── */}
            <section className="space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800">
              <h4 className="font-medium text-slate-800 dark:text-slate-200">最近的推送</h4>
              {deliveries.length === 0 ? (
                <p className="text-slate-500">还没有。</p>
              ) : (
                <ul className="space-y-1.5 text-xs">
                  {deliveries.map((d) => (
                    <li
                      key={d.id}
                      className="flex flex-wrap items-baseline gap-x-2 rounded-lg border border-slate-100 p-2 dark:border-slate-800"
                    >
                      <span className="font-mono text-slate-500">{d.orderNo ?? d.event}</span>
                      <span className="text-slate-700 dark:text-slate-200">
                        {d.orderStatus ?? d.event}
                      </span>
                      <span
                        className={
                          d.status === 'SUCCEEDED'
                            ? 'text-green-700 dark:text-green-400'
                            : d.status === 'DEAD'
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-slate-500'
                        }
                      >
                        {DELIVERY_LABEL[d.status]}
                        {d.attempts > 1 && ` · 试了 ${d.attempts} 次`}
                      </span>
                      {d.lastError && d.status !== 'SUCCEEDED' && (
                        <span className="text-slate-500">{d.lastError}</span>
                      )}
                      <span className="ml-auto text-slate-400">
                        {new Date(d.createdAt).toLocaleString()}
                      </span>
                      {(d.status === 'DEAD' ||
                        d.status === 'SUCCEEDED' ||
                        d.status === 'CANCELLED') && (
                        <button
                          onClick={() => void retry(d)}
                          disabled={busy !== null}
                          className="text-brand-600 underline disabled:opacity-40 dark:text-brand-500"
                        >
                          重发
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <p className="border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-slate-800">
              接口地址 <code className="font-mono">{apiBase}/ext/orders</code>。签名方法、字段说明、
              推送格式和要回什么,见对接文档(docs/open-api.md),直接发给对方的技术。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const INPUT =
  'w-full h-9 px-2 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm';
